import { definePreset } from '@primeuix/themes'
import Aura from '@primevue/themes/aura'

const PrecisionAuthority = definePreset(Aura, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '1px',
      sm: '2px',
      md: '4px',
      lg: '6px',
      xl: '8px'
    }
  },
  semantic: {
    transitionDuration: '0.15s',
    focusRing: {
      width: '1px',
      style: 'solid',
      color: '{primary.color}',
      offset: '2px',
      shadow: 'none'
    },
    primary: {
      50: '#fafafa',
      100: '#f5f5f5',
      200: '#e5e5e5',
      300: '#d4d4d4',
      400: '#a3a3a3',
      500: '#000000',
      600: '#000000',
      700: '#000000',
      800: '#1c1b1b',
      900: '#0b1c30',
      950: '#050d18'
    },
    formField: {
      borderRadius: '{border.radius.sm}',
      focusRing: {
        width: '0',
        style: 'none',
        color: 'transparent',
        offset: '0',
        shadow: 'none'
      }
    },
    content: {
      borderRadius: '{border.radius.sm}'
    },
    overlay: {
      select: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 8px 32px rgba(11, 28, 48, 0.06)'
      },
      popover: {
        borderRadius: '{border.radius.sm}',
        shadow: '0 8px 32px rgba(11, 28, 48, 0.06)'
      },
      modal: {
        borderRadius: '{border.radius.md}',
        shadow: '0 8px 32px rgba(11, 28, 48, 0.08)'
      },
      navigation: {
        shadow: '0 8px 32px rgba(11, 28, 48, 0.06)'
      }
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f8f9ff',
          100: '#eff4ff',
          200: '#e5eeff',
          300: '#dce9ff',
          400: '#d3e4fe',
          500: '#cbdbf5',
          600: '#747878',
          700: '#444748',
          800: '#213145',
          900: '#0b1c30',
          950: '#050d18'
        },
        primary: {
          color: '#000000',
          contrastColor: '#ffffff',
          hoverColor: '#1c1b1b',
          activeColor: '#0b1c30'
        },
        highlight: {
          background: '#eff4ff',
          focusBackground: '#e5eeff',
          color: '#0b1c30',
          focusColor: '#0b1c30'
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '#eff4ff',
          filledBackground: '#f8f9ff',
          filledHoverBackground: '#eff4ff',
          filledFocusBackground: '#ffffff',
          borderColor: '#c4c7c7',
          hoverBorderColor: '#747878',
          focusBorderColor: '#000000',
          invalidBorderColor: '#ba1a1a',
          color: '#0b1c30',
          disabledColor: '#747878',
          placeholderColor: '#747878',
          floatLabelColor: '#747878',
          floatLabelFocusColor: '#000000',
          floatLabelActiveColor: '#444748',
          iconColor: '#747878',
          shadow: 'none'
        },
        text: {
          color: '#0b1c30',
          hoverColor: '#000000',
          mutedColor: '#444748',
          hoverMutedColor: '#0b1c30'
        },
        content: {
          background: '#ffffff',
          hoverBackground: '#eff4ff',
          borderColor: 'rgba(196, 199, 199, 0.2)',
          color: '#0b1c30',
          hoverColor: '#000000'
        },
        overlay: {
          select: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.2)',
            color: '#0b1c30'
          },
          popover: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.2)',
            color: '#0b1c30'
          },
          modal: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.2)',
            color: '#0b1c30'
          }
        },
        list: {
          option: {
            focusBackground: '#eff4ff',
            selectedBackground: '#e5eeff',
            selectedFocusBackground: '#dce9ff',
            color: '#0b1c30',
            focusColor: '#000000',
            selectedColor: '#0b1c30',
            selectedFocusColor: '#000000',
            icon: {
              color: '#747878',
              focusColor: '#444748'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '#444748'
          }
        },
        navigation: {
          item: {
            focusBackground: '#eff4ff',
            activeBackground: '#e5eeff',
            color: '#0b1c30',
            focusColor: '#000000',
            activeColor: '#000000',
            icon: {
              color: '#747878',
              focusColor: '#444748',
              activeColor: '#000000'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '#444748'
          },
          submenuIcon: {
            color: '#747878',
            focusColor: '#444748',
            activeColor: '#000000'
          }
        }
      },
      dark: {
        surface: {
          0: '#0b1c30',
          50: '#111d2e',
          100: '#162236',
          200: '#1d2d42',
          300: '#243850',
          400: '#2d4562',
          500: '#3a5577',
          600: '#8899ad',
          700: '#a3b5c9',
          800: '#c0d0e2',
          900: '#dde8f3',
          950: '#eaf1ff'
        },
        primary: {
          color: '#c8c6c5',
          contrastColor: '#0b1c30',
          hoverColor: '#e5e2e1',
          activeColor: '#ffffff'
        },
        highlight: {
          background: 'rgba(200, 198, 197, 0.16)',
          focusBackground: 'rgba(200, 198, 197, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        },
        formField: {
          background: '#162236',
          disabledBackground: '#1d2d42',
          filledBackground: '#111d2e',
          filledHoverBackground: '#162236',
          filledFocusBackground: '#162236',
          borderColor: '#2d4562',
          hoverBorderColor: '#3a5577',
          focusBorderColor: '#c8c6c5',
          invalidBorderColor: '#ffdad6',
          color: '#eaf1ff',
          disabledColor: '#8899ad',
          placeholderColor: '#8899ad',
          floatLabelColor: '#8899ad',
          floatLabelFocusColor: '#c8c6c5',
          floatLabelActiveColor: '#a3b5c9',
          iconColor: '#8899ad',
          shadow: 'none'
        },
        text: {
          color: '#eaf1ff',
          hoverColor: '#ffffff',
          mutedColor: '#a3b5c9',
          hoverMutedColor: '#c0d0e2'
        },
        content: {
          background: '#111d2e',
          hoverBackground: '#1d2d42',
          borderColor: 'rgba(45, 69, 98, 0.4)',
          color: '#eaf1ff',
          hoverColor: '#ffffff'
        },
        overlay: {
          select: {
            background: '#162236',
            borderColor: 'rgba(45, 69, 98, 0.4)',
            color: '#eaf1ff'
          },
          popover: {
            background: '#162236',
            borderColor: 'rgba(45, 69, 98, 0.4)',
            color: '#eaf1ff'
          },
          modal: {
            background: '#162236',
            borderColor: 'rgba(45, 69, 98, 0.4)',
            color: '#eaf1ff'
          }
        },
        list: {
          option: {
            focusBackground: '#1d2d42',
            selectedBackground: 'rgba(200, 198, 197, 0.16)',
            selectedFocusBackground: 'rgba(200, 198, 197, 0.24)',
            color: '#eaf1ff',
            focusColor: '#ffffff',
            selectedColor: 'rgba(255,255,255,.87)',
            selectedFocusColor: 'rgba(255,255,255,.87)',
            icon: {
              color: '#8899ad',
              focusColor: '#a3b5c9'
            }
          },
          optionGroup: {
            background: 'transparent',
            color: '#a3b5c9'
          }
        },
        navigation: {
          item: {
            focusBackground: '#1d2d42',
            activeBackground: '#243850',
            color: '#eaf1ff',
            focusColor: '#ffffff',
            activeColor: '#ffffff',
            icon: {
              color: '#8899ad',
              focusColor: '#a3b5c9',
              activeColor: '#c8c6c5'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '#a3b5c9'
          },
          submenuIcon: {
            color: '#8899ad',
            focusColor: '#a3b5c9',
            activeColor: '#c8c6c5'
          }
        }
      }
    }
  },
  components: {
    button: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          root: {
            primary: {
              background: '#000000',
              hoverBackground: '#1c1b1b',
              activeBackground: '#0b1c30',
              borderColor: '#000000',
              hoverBorderColor: '#1c1b1b',
              activeBorderColor: '#0b1c30',
              color: '#ffffff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            },
            secondary: {
              background: '#dce9ff',
              hoverBackground: '#d3e4fe',
              activeBackground: '#cbdbf5',
              borderColor: '#dce9ff',
              hoverBorderColor: '#d3e4fe',
              activeBorderColor: '#cbdbf5',
              color: '#0b1c30',
              hoverColor: '#000000',
              activeColor: '#000000'
            }
          }
        },
        dark: {
          root: {
            primary: {
              background: '#c0d0e2',
              hoverBackground: '#dde8f3',
              activeBackground: '#eaf1ff',
              borderColor: '#c0d0e2',
              hoverBorderColor: '#dde8f3',
              activeBorderColor: '#eaf1ff',
              color: '#0b1c30',
              hoverColor: '#050d18',
              activeColor: '#050d18'
            },
            secondary: {
              background: '#1d2d42',
              hoverBackground: '#243850',
              activeBackground: '#2d4562',
              borderColor: '#2d4562',
              hoverBorderColor: '#3a5577',
              activeBorderColor: '#3a5577',
              color: '#eaf1ff',
              hoverColor: '#ffffff',
              activeColor: '#ffffff'
            }
          }
        }
      }
    },
    card: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            color: '#0b1c30'
          },
          subtitle: {
            color: '#444748'
          }
        },
        dark: {
          root: {
            background: '#162236',
            color: '#eaf1ff'
          },
          subtitle: {
            color: '#a3b5c9'
          }
        }
      }
    },
    datatable: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          root: {
            borderColor: 'rgba(196, 199, 199, 0.1)'
          },
          header: {
            background: '#eff4ff',
            color: '#444748',
            borderColor: 'rgba(196, 199, 199, 0.1)'
          },
          headerCell: {
            background: 'transparent',
            hoverBackground: '#e5eeff',
            color: '#444748',
            hoverColor: '#0b1c30',
            borderColor: 'rgba(196, 199, 199, 0.1)'
          },
          row: {
            background: '#ffffff',
            hoverBackground: '#eff4ff',
            color: '#0b1c30',
            hoverColor: '#000000',
            borderColor: 'rgba(196, 199, 199, 0.1)'
          },
          bodyCell: {
            borderColor: 'rgba(196, 199, 199, 0.1)'
          },
          footer: {
            background: '#eff4ff',
            color: '#444748',
            borderColor: 'rgba(196, 199, 199, 0.1)'
          }
        },
        dark: {
          root: {
            borderColor: 'rgba(45, 69, 98, 0.3)'
          },
          header: {
            background: '#111d2e',
            color: '#a3b5c9',
            borderColor: 'rgba(45, 69, 98, 0.3)'
          },
          headerCell: {
            background: 'transparent',
            hoverBackground: '#1d2d42',
            color: '#a3b5c9',
            hoverColor: '#eaf1ff',
            borderColor: 'rgba(45, 69, 98, 0.3)'
          },
          row: {
            background: '#162236',
            hoverBackground: '#1d2d42',
            color: '#eaf1ff',
            hoverColor: '#ffffff',
            borderColor: 'rgba(45, 69, 98, 0.3)'
          },
          bodyCell: {
            borderColor: 'rgba(45, 69, 98, 0.3)'
          },
          footer: {
            background: '#111d2e',
            color: '#a3b5c9',
            borderColor: 'rgba(45, 69, 98, 0.3)'
          }
        }
      }
    },
    dialog: {
      borderRadius: '{border.radius.md}',
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.1)',
            color: '#0b1c30'
          },
          header: {
            background: '#ffffff',
            color: '#0b1c30'
          }
        },
        dark: {
          root: {
            background: '#162236',
            borderColor: 'rgba(45, 69, 98, 0.4)',
            color: '#eaf1ff'
          },
          header: {
            background: '#162236',
            color: '#eaf1ff'
          }
        }
      }
    },
    inputtext: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.3)',
            hoverBorderColor: '#747878',
            focusBorderColor: '#000000',
            color: '#0b1c30'
          }
        },
        dark: {
          root: {
            background: '#1d2d42',
            borderColor: 'rgba(45, 69, 98, 0.5)',
            hoverBorderColor: '#3a5577',
            focusBorderColor: '#c8c6c5',
            color: '#eaf1ff'
          }
        }
      }
    },
    select: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        dark: {
          root: {
            background: '#1d2d42',
            borderColor: 'rgba(45, 69, 98, 0.5)',
            hoverBorderColor: '#3a5577',
            color: '#eaf1ff'
          }
        }
      }
    },
    multiselect: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        dark: {
          root: {
            background: '#1d2d42',
            borderColor: 'rgba(45, 69, 98, 0.5)',
            hoverBorderColor: '#3a5577',
            color: '#eaf1ff'
          }
        }
      }
    },
    tag: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          success: {
            background: '#8df9a8',
            color: '#007439'
          },
          warn: {
            background: '#ffdcc3',
            color: '#6e3900'
          },
          danger: {
            background: '#ffdad6',
            color: '#93000a'
          },
          info: {
            background: '#e5eeff',
            color: '#0b1c30'
          }
        },
        dark: {
          success: {
            background: 'rgba(113, 220, 142, 0.2)',
            color: '#71dc8e'
          },
          warn: {
            background: 'rgba(255, 183, 125, 0.2)',
            color: '#ffb77d'
          },
          danger: {
            background: 'rgba(186, 26, 26, 0.2)',
            color: '#ffdad6'
          },
          info: {
            background: 'rgba(45, 69, 98, 0.4)',
            color: '#c0d0e2'
          }
        }
      }
    },
    toast: {
      borderRadius: '{border.radius.sm}'
    },
    menu: {
      borderRadius: '{border.radius.sm}',
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            borderColor: 'rgba(196, 199, 199, 0.2)',
            color: '#0b1c30',
            shadow: '0 8px 32px rgba(11, 28, 48, 0.06)'
          }
        },
        dark: {
          root: {
            background: '#162236',
            borderColor: 'rgba(45, 69, 98, 0.4)',
            color: '#eaf1ff',
            shadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }
        }
      }
    },
    panelmenu: {
      borderRadius: '{border.radius.sm}'
    },
    progressbar: {
      borderRadius: '{border.radius.sm}'
    }
  }
})

export default PrecisionAuthority
